import moment from "moment";
import useTranslator from "../../hooks/useTranslator";
import { formatPropertyAddress, haveValue } from "../../helper/common";

const ClockIcon = ({ size = 28 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

// "We're finishing your policy": the landlord submitted the beneficiary form
// and the policy is finished outside the automatic flow. variant "failure"
// (MDP steps 4/5 failed, retried / Ops) or "review" (manual review case).
const InsuranceBeingIssuedPopup = ({
  show,
  onClose,
  propertyData,
  submittedDetails,
  variant = "review",
}) => {
  const { _t } = useTranslator();

  if (!show) return null;

  const currentLang = localStorage.getItem("i18nextLng") || "en-us";
  const propertyAddress = formatPropertyAddress(propertyData, currentLang);
  const paymentAmount = propertyData?.insurance_data?.payment_amount;
  const paymentDate = propertyData?.insurance_payment_date;
  const beneficiaryText = (submittedDetails?.beneficiaries || [])
    .map(({ name, documentNumber }) =>
      [name, documentNumber].filter(Boolean).join(" · ")
    )
    .filter(Boolean)
    .join(", ");
  const policyStart = submittedDetails?.policyStartDate
    ? moment(submittedDetails.policyStartDate, [
        "DD/MM/YYYY",
        moment.ISO_8601,
      ]).format("D MMM YYYY")
    : null;

  const rows = [
    haveValue(propertyAddress) && {
      label: _t("insuranceReviewPropertyLabel"),
      value: propertyAddress,
    },
    haveValue(paymentAmount) && {
      label: _t("insurancePremium"),
      value: `${paymentAmount} €${
        haveValue(paymentDate)
          ? ` · ${_t("paid")} ${moment(paymentDate).format("D MMM YYYY")}`
          : ""
      }`,
    },
    haveValue(beneficiaryText) && {
      label: _t("beneficiary"),
      value: beneficiaryText,
    },
    haveValue(policyStart) && { label: _t("policyStart"), value: policyStart },
    haveValue(submittedDetails?.contractFileName) && {
      label: _t("rentalContract"),
      value: submittedDetails.contractFileName,
      // File names can be very long: one line with "…", full name on hover.
      truncate: true,
    },
  ].filter(Boolean);

  return (
    <div
      className="popup-overlay insurance-payment-success-overlay fixed inset-0 d-flex align-items-center justify-content-center z-50 wrapper-modal"
      style={{ zIndex: 9999 }}
    >
      <div className="ins-activated-card ins-payment-success-card ins-being-issued-card">
        <button
          type="button"
          className="ins-activated-close"
          onClick={onClose}
          aria-label={_t("close")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>

        <div className="ins-activated-icon-wrap ins-being-issued-icon">
          <ClockIcon />
        </div>

        <h2 className="ins-activated-heading">
          {_t("insuranceFinishingYourPolicy")}
        </h2>
        <p className="ins-activated-subheading ins-payment-success-subheading">
          {variant === "failure"
            ? _t("insuranceCouldNotIssueAutomaticallyText")
            : _t("insuranceBeingIssuedText")}
        </p>

        <div className="ins-activated-details-card ins-payment-success-details">
          {rows.map((row) => (
            <div key={row.label} className="ins-activated-row">
              <span className="ins-activated-label">{row.label}</span>
              <span
                className={`ins-activated-value-bold ${
                  row.truncate ? "ins-being-issued-truncate" : ""
                }`}
                title={row.value}
              >
                {row.value}
              </span>
            </div>
          ))}
          <div className="ins-activated-row">
            <span className="ins-activated-label">{_t("policy")}</span>
            <span className="insurance-under-review-chip">
              <ClockIcon size={14} />
              {_t("insuranceBeingIssued")}
            </span>
          </div>
        </div>

        <div className="ins-payment-success-note ins-payment-success-note--review">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
          <div>
            <p>{_t("insuranceNothingElseToDoWeWillEmail")}</p>
          </div>
        </div>

        <div className="ins-payment-success-actions">
          <button
            type="button"
            className="btnNext addGuarantorButton ins-payment-success-done"
            onClick={onClose}
          >
            {_t("done")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsuranceBeingIssuedPopup;
